import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "../api";
import { onAuthedFileClick } from "../mobileFiles";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle } from "../components/Layout";
import { Modal, fmtEur } from "./AdminSections";
import {
  GraduationCap, ChevronLeft, FileText, Download, Upload, MessageSquare, Award,
  Plus, Trash2, Wallet, CheckCircle2, RotateCcw, Users,
} from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;
const fileHref = (u) => (!u ? "#" : u.startsWith("http") ? u : `${BE}${u}`);
const inp = "w-full border border-gray-200 rounded-xl px-3 py-2 mt-1 text-sm";
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const readFileDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const tutoringLabel = (t, assistActive) => {
  if (assistActive) return { txt: "Assistenza post-corso attiva", cls: "bg-violet-100 text-violet-700" };
  if (!t || !t.enabled) return { txt: "Tutoraggio non attivo", cls: "bg-gray-100 text-gray-500" };
  if (t.status === "not_started") return { txt: "Tutoraggio non ancora iniziato", cls: "bg-gray-100 text-gray-600" };
  if (t.status === "expired") return { txt: "Tutoraggio scaduto", cls: "bg-gray-100 text-gray-500" };
  if (t.status === "active") {
    const days = t.end_at ? Math.ceil((new Date(t.end_at) - new Date()) / 86400000) : 99;
    if (days <= 7) return { txt: `Tutoraggio in scadenza (${days}g)`, cls: "bg-amber-100 text-amber-700" };
    return { txt: "Tutoraggio attivo", cls: "bg-green-100 text-green-700" };
  }
  return { txt: t.status, cls: "bg-gray-100 text-gray-500" };
};

/* ============================ ROOT ============================ */
export default function TeacherDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("corsi");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !(user.roles || []).includes("teacher"))) nav("/");
  }, [user, loading]); // eslint-disable-line

  if (loading || !user) return <Page><div className="text-center text-gray-400 pt-20">…</div></Page>;
  if (!(user.roles || []).includes("teacher")) return null;

  return (
    <Page>
      <SectionTitle sub="Area Docente MAS Academy — corsi, allievi, tutoraggio e compenso.">Area Docente</SectionTitle>
      {selected ? (
        <CourseView cid={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          <div className="mb-5">
            <select data-testid="teacher-tab-select" value={tab} onChange={(e) => setTab(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium bg-white">
              <option value="corsi">I miei corsi</option>
              <option value="compenso">Il mio compenso</option>
              <option value="profilo">Il mio profilo</option>
            </select>
          </div>
          {tab === "corsi" && <CoursesList onOpen={setSelected} />}
          {tab === "compenso" && <EarningsView />}
          {tab === "profilo" && <ProfileView />}
        </>
      )}
    </Page>
  );
}

/* ============================ COURSES LIST ============================ */
function CoursesList({ onOpen }) {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/academy/teacher/courses").then((r) => setCourses(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  return (
    <div data-testid="teacher-courses">
      <div className="flex items-center gap-2 mb-3 text-violet-700"><GraduationCap size={18} /><span className="text-sm font-semibold">I miei corsi</span></div>
      <div className="space-y-3">
        {courses.map((c) => (
          <div key={c.id} className="card p-3" data-testid={`teacher-course-${c.id}`}>
            <div className="font-semibold text-sm">{c.title}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.duration_days ? `${c.duration_days} giornate` : "durata n.d."} · {c.days_count} create · quota docente {c.teacher_pct ?? "—"}%</div>
            <div className="text-[11px] text-gray-400 mt-0.5">Tutoraggio {c.tutoring_enabled ? `${c.tutoring_duration_days || 90}gg` : "no"} · Assistenza {c.assistance_enabled ? "sì" : "no"} · Level Up {c.level_up_enabled ? "sì" : "no"}</div>
            <button data-testid={`teacher-open-${c.id}`} onClick={() => onOpen(c.id)} className="btn-primary text-xs py-1 px-3 mt-2">Apri corso</button>
          </div>
        ))}
        {courses.length === 0 && <p className="text-sm text-gray-400">Non hai ancora corsi assegnati.</p>}
      </div>
    </div>
  );
}

/* ============================ COURSE VIEW ============================ */
function CourseView({ cid, onBack }) {
  const [detail, setDetail] = useState(null);
  const [sub, setSub] = useState("allievi");
  const load = useCallback(() => { api.get(`/academy/teacher/courses/${cid}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e))); }, [cid]);
  useEffect(() => { load(); }, [load]);
  if (!detail) return <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div>;
  return (
    <div data-testid="teacher-course-view">
      <button data-testid="teacher-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-violet-700 mb-3"><ChevronLeft size={16} /> I miei corsi</button>
      <div className="card p-3 mb-3"><div className="font-semibold text-sm">{detail.title}</div><div className="text-xs text-gray-500 mt-0.5">Quota docente {detail.teacher_pct}%{detail.assistance_enabled ? ` · assistenza docente ${detail.support_teacher_pct}%` : ""}</div></div>
      <select data-testid="teacher-subsection" value={sub} onChange={(e) => setSub(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium bg-white mb-4">
        <option value="allievi">Allievi</option>
        <option value="giornate">Giornate & Materiali</option>
        <option value="esercizi">Esercizi</option>
      </select>
      {sub === "allievi" && <StudentsList course={detail} />}
      {sub === "giornate" && <DaysView days={detail.days || []} />}
      {sub === "esercizi" && <ExercisesEditor cid={cid} days={detail.days || []} />}
    </div>
  );
}

/* ---------- Days (read-only) ---------- */
function DaysView({ days }) {
  return (
    <div data-testid="teacher-days">
      {days.map((d) => (
        <div key={d.id} className="card p-3 mb-3" data-testid={`teacher-day-${d.id}`}>
          <div className="font-semibold text-sm">Giornata {(d.day_index ?? 0) + 1} · {d.title}</div>
          {d.description && <div className="text-xs text-gray-500 mt-0.5">{d.description}</div>}
          {d.program && <div className="text-xs text-gray-500 mt-1"><b>Programma:</b> {d.program}</div>}
          <div className="mt-2 space-y-1">
            {(d.materials || []).map((m) => (
              <a key={m.id} href={fileHref(`/api/academy/materials/${m.id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/materials/${m.id}/file`, m.filename)} className="flex justify-between items-center text-[11px] bg-gray-50 rounded-lg px-2 py-1 text-violet-700">
                <span className="truncate"><FileText size={11} className="inline mr-1" />{m.filename}</span><Download size={13} />
              </a>
            ))}
            {(d.materials || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun materiale.</div>}
          </div>
        </div>
      ))}
      {days.length === 0 && <p className="text-sm text-gray-400">Nessuna giornata.</p>}
    </div>
  );
}

/* ---------- Exercises (teacher can CRUD own) ---------- */
function ExercisesEditor({ cid, days }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => { api.get(`/academy/courses/${cid}/exercises`).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))); }, [cid]);
  useEffect(() => { load(); }, [load]);
  const dayLabel = (id) => { const d = days.find((x) => x.id === id); return d ? `G${(d.day_index ?? 0) + 1}` : "—"; };
  const save = async () => {
    if (!form.day_id) return toast.error("Seleziona la giornata");
    if (!form.title?.trim()) return toast.error("Titolo obbligatorio");
    try {
      if (form.id) await api.put(`/academy/exercises/${form.id}`, { title: form.title, instructions: form.instructions, required: form.required, active: form.active });
      else await api.post(`/academy/courses/${cid}/exercises`, { day_id: form.day_id, title: form.title, instructions: form.instructions, required: form.required, active: form.active });
      toast.success("Esercizio salvato"); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="teacher-exercises">
      <button data-testid="teacher-exercise-new" disabled={days.length === 0} onClick={() => setForm({ day_id: days[0]?.id || "", title: "", instructions: "", required: true, active: true })} className="btn-primary text-sm w-full mb-3 inline-flex items-center justify-center gap-2"><Plus size={16} /> Nuovo esercizio</button>
      <div className="space-y-2">
        {list.map((x) => (
          <div key={x.id} className="card p-3" data-testid={`teacher-exercise-${x.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0"><div className="font-semibold text-sm">{x.title}</div><div className="text-[11px] text-gray-500">{dayLabel(x.day_id)} · {x.required ? "obbligatorio" : "facoltativo"} · {x.active === false ? "non attivo" : "attivo"}</div></div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`teacher-exercise-edit-${x.id}`} onClick={() => setForm(x)} className="btn-outline text-xs py-1 px-2">Modifica</button>
                <button data-testid={`teacher-exercise-del-${x.id}`} onClick={() => setDel(x)} className="btn-outline text-xs py-1 px-2"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-gray-400">Nessun esercizio.</p>}
      </div>
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Modifica esercizio" : "Nuovo esercizio"}>
        {form && <div className="space-y-2">
          <label className="block text-xs text-gray-500">Giornata<select data-testid="teacher-exercise-day" className={inp} value={form.day_id || ""} disabled={!!form.id} onChange={(e) => setForm({ ...form, day_id: e.target.value })}>{days.map((d) => <option key={d.id} value={d.id}>G{(d.day_index ?? 0) + 1} · {d.title}</option>)}</select></label>
          <label className="block text-xs text-gray-500">Titolo<input data-testid="teacher-exercise-title" className={inp} value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label className="block text-xs text-gray-500">Istruzioni<textarea className={inp} rows={2} value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.required !== false} onChange={(e) => setForm({ ...form, required: e.target.checked })} />Obbligatorio (Level Up)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />Attivo</label>
          <div className="flex gap-2 pt-1"><button onClick={() => setForm(null)} className="btn-outline flex-1 text-sm">Annulla</button><button data-testid="teacher-exercise-save" onClick={save} className="btn-primary flex-1 text-sm">Salva</button></div>
        </div>}
      </Modal>
      <Modal open={!!del} onClose={() => setDel(null)} title="Eliminare l'esercizio?">
        <p className="text-sm text-gray-600 mb-3">Verranno eliminate anche le consegne collegate.</p>
        <div className="flex gap-2"><button onClick={() => setDel(null)} className="btn-outline flex-1 text-sm">Annulla</button><button data-testid="teacher-exercise-del-confirm" onClick={async () => { const x = del; setDel(null); try { await api.delete(`/academy/exercises/${x.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(apiErr(e)); } }} className="btn-primary flex-1 text-sm bg-red-600 border-red-600">Elimina</button></div>
      </Modal>
    </div>
  );
}

/* ---------- Students list ---------- */
function StudentsList({ course }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get(`/academy/teacher/courses/${course.id}/students`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [course.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="teacher-students">
      <div className="flex items-center gap-2 mb-2 text-violet-700"><Users size={16} /><span className="text-sm font-semibold">{rows.length} allievi</span></div>
      <div className="space-y-3">
        {rows.map((s) => {
          const tl = tutoringLabel(s.tutoring, s.assistance_active);
          return (
            <div key={s.enrollment_id} className="card p-3" data-testid={`teacher-student-${s.enrollment_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0"><div className="font-semibold text-sm truncate">{s.name || "—"}</div><div className="text-[11px] text-gray-500">{s.status === "paid" ? "iscrizione pagata" : "in attesa di pagamento"}</div></div>
                {s.status === "paid" && <span className={`text-[10px] px-2 py-1 rounded-full shrink-0 ${tl.cls}`}>{tl.txt}</span>}
              </div>
              {s.status === "paid" && <button data-testid={`teacher-student-open-${s.enrollment_id}`} onClick={() => setOpen(open === s.enrollment_id ? null : s.enrollment_id)} className="btn-outline text-xs py-1 px-2 mt-2">{open === s.enrollment_id ? "Chiudi" : "Percorso allievo"}</button>}
              {open === s.enrollment_id && <StudentDetail student={s} course={course} reload={load} />}
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-sm text-gray-400">Nessun allievo.</p>}
      </div>
    </div>
  );
}

function StudentDetail({ student, course, reload }) {
  const eid = student.enrollment_id;
  const [view, setView] = useState("esercizi");
  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-3" data-testid={`teacher-student-detail-${eid}`}>
      {student.ledger && (
        <div className="rounded-xl bg-gray-50 p-2 text-[11px] space-y-0.5">
          <div className="flex justify-between"><span className="text-gray-500">Quota docente maturata</span><span className="text-gray-800">{fmtEur(student.ledger.teacher_accrued)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Corrisposta</span><span className="text-gray-800">{fmtEur(student.ledger.teacher_paid)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Da corrispondere</span><span className="font-semibold text-gray-900">{fmtEur(Math.max(0, student.ledger.teacher_due))}</span></div>
        </div>
      )}
      <div className="flex gap-1 flex-wrap">
        {[["esercizi", "Esercizi/Consegne"], ["tutoraggio", "Tutoraggio"], ["attestati", "Attestati"]].map(([k, l]) => (
          <button key={k} data-testid={`teacher-sd-${k}-${eid}`} onClick={() => setView(k)} className={`text-xs py-1 px-2 rounded-lg ${view === k ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-600"}`}>{l}</button>
        ))}
      </div>
      {view === "esercizi" && <SubmissionsReview cid={course.id} studentId={student.user_id} />}
      {view === "tutoraggio" && <TutoringChat eid={eid} />}
      {view === "attestati" && <CertificatesBlock eid={eid} cid={course.id} studentId={student.user_id} course={course} />}
    </div>
  );
}

/* ---------- Submissions review ---------- */
function SubmissionsReview({ cid, studentId }) {
  const [exs, setExs] = useState([]);
  const [subsByEx, setSubsByEx] = useState({});
  const [fb, setFb] = useState({});
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/academy/courses/${cid}/exercises`);
      setExs(r.data);
      const map = {};
      for (const x of r.data) {
        const s = await api.get(`/academy/exercises/${x.id}/submissions?student_id=${studentId}`);
        map[x.id] = s.data;
      }
      setSubsByEx(map);
    } catch (e) { toast.error(apiErr(e)); }
  }, [cid, studentId]);
  useEffect(() => { load(); }, [load]);
  const review = async (sid, action) => {
    try { await api.post(`/academy/submissions/${sid}/review`, { action, feedback: fb[sid] || "" }); toast.success(action === "approved" ? "Consegna approvata" : "Richiesta correzione inviata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="teacher-submissions" className="space-y-3">
      {exs.map((x) => {
        const subs = subsByEx[x.id] || [];
        const latest = subs[subs.length - 1];
        return (
          <div key={x.id} className="rounded-xl border border-gray-100 p-2">
            <div className="text-sm font-semibold">{x.title} {x.required ? <span className="text-[10px] text-gray-400">(obbligatorio)</span> : null}</div>
            {subs.length === 0 && <div className="text-[11px] text-gray-400 mt-1">Nessuna consegna.</div>}
            {subs.map((s) => (
              <div key={s.id} className="mt-2 bg-gray-50 rounded-lg p-2" data-testid={`submission-${s.id}`}>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Versione {s.version} · {s.status === "approved" ? "approvata" : s.status === "needs_fix" ? "da correggere" : "in revisione"} · {fmtD(s.created_at)}</div>
                {s.text && <div className="text-xs text-gray-800 mt-1 whitespace-pre-wrap">{s.text}</div>}
                {(s.attachments || []).map((a, i) => <a key={i} href={fileHref(`/api/academy/uploads/${a.upload_id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/uploads/${a.upload_id}/file`, a.filename)} className="text-violet-700 text-[11px] inline-flex items-center gap-1 mt-1 mr-2"><Download size={11} /> {a.filename || "allegato"}</a>)}
                {s.teacher_feedback && <div className="text-[11px] text-gray-600 mt-1"><b>Feedback:</b> {s.teacher_feedback}</div>}
              </div>
            ))}
            {latest && latest.status !== "approved" && (
              <div className="mt-2 space-y-1" data-testid={`review-box-${x.id}`}>
                <textarea data-testid={`feedback-${latest.id}`} className={inp} rows={2} placeholder="Feedback per l'allievo" value={fb[latest.id] || ""} onChange={(e) => setFb({ ...fb, [latest.id]: e.target.value })} />
                <div className="flex gap-2">
                  <button data-testid={`needsfix-${latest.id}`} onClick={() => review(latest.id, "needs_fix")} className="btn-outline text-xs py-1 px-2 inline-flex items-center gap-1"><RotateCcw size={12} /> Da correggere</button>
                  <button data-testid={`approve-${latest.id}`} onClick={() => review(latest.id, "approved")} className="btn-primary text-xs py-1 px-2 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Approva</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {exs.length === 0 && <p className="text-sm text-gray-400">Nessun esercizio nel corso.</p>}
    </div>
  );
}

/* ---------- Tutoring chat ---------- */
function TutoringChat({ eid }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/academy/enrollments/${eid}/tutoring`);
      let msgs = [];
      if (r.data.thread?.id) { const m = await api.get(`/academy/tutoring/${r.data.thread.id}/messages`); msgs = m.data; }
      setState({ tutoring: r.data.tutoring, thread: r.data.thread, msgs });
    } catch (e) { toast.error(apiErr(e)); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);
  const pick = async (e) => {
    const fs = Array.from(e.target.files || []);
    const out = [];
    for (const f of fs) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (max 8MB)`); continue; }
      out.push({ data: await readFileDataUrl(f), filename: f.name });
    }
    setFiles(out); e.target.value = "";
  };
  const send = async () => {
    if (!text.trim() && files.length === 0) return toast.error("Messaggio vuoto");
    setBusy(true);
    try { await api.post(`/academy/tutoring/${state.thread.id}/messages`, { text, attachments: files }); setText(""); setFiles([]); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!state) return <div className="text-xs text-gray-400">Carico…</div>;
  if (!state.thread) return <div className="text-xs text-amber-600">Tutoraggio non abilitato per questo corso.</div>;
  return (
    <div data-testid="teacher-tutoring">
      <div className="max-h-[45vh] overflow-y-auto space-y-2 mb-2" data-testid="teacher-tutoring-messages">
        {(state.msgs || []).map((m) => (
          <div key={m.id} className={`text-xs rounded-xl p-2 ${m.sender_role === "teacher" ? "bg-violet-50 ml-6" : "bg-gray-50 mr-6"}`}>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{m.sender_role === "teacher" ? "Tu (Docente)" : m.sender_role === "student" ? "Allievo" : "MAS"} · {fmtD(m.created_at)}</div>
            {m.text && <div className="text-gray-800 mt-0.5 whitespace-pre-wrap">{m.text}</div>}
            {(m.attachments || []).map((a, i) => <a key={i} href={fileHref(`/api/academy/uploads/${a.upload_id}/file`)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, `/api/academy/uploads/${a.upload_id}/file`, a.filename)} className="text-violet-700 inline-flex items-center gap-1 mt-1"><Download size={11} /> {a.filename || "allegato"}</a>)}
          </div>
        ))}
        {(state.msgs || []).length === 0 && <div className="text-[11px] text-gray-400">Nessun messaggio.</div>}
      </div>
      <div className="space-y-1">
        <textarea data-testid="teacher-tutoring-input" className={inp} rows={2} placeholder="Scrivi all'allievo…" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="flex items-center gap-2">
          <label className="btn-outline text-xs cursor-pointer inline-flex items-center gap-1" data-testid="teacher-tutoring-attach"><Upload size={12} /> Allega{files.length ? ` (${files.length})` : ""}<input type="file" multiple accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={pick} /></label>
          <button data-testid="teacher-tutoring-send" disabled={busy} onClick={send} className="btn-primary text-xs py-1 px-3 ml-auto">{busy ? "Invio…" : "Invia"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Certificates ---------- */
function CertificatesBlock({ eid, cid, studentId, course }) {
  const [certs, setCerts] = useState([]);
  const [status, setStatus] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(async () => {
    try {
      const c = await api.get(`/academy/enrollments/${eid}/certificates`); setCerts(c.data);
      const s = await api.get(`/academy/courses/${cid}/exercise-status?student_id=${studentId}`); setStatus(s.data);
    } catch (e) { toast.error(apiErr(e)); }
  }, [eid, cid, studentId]);
  useEffect(() => { load(); }, [load]);
  const issueFreq = async () => { try { await api.post(`/academy/enrollments/${eid}/certificate/frequency`); toast.success("Attestato di frequenza emesso"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const issueLevelUp = async () => { setConfirm(false); try { await api.post(`/academy/enrollments/${eid}/certificate/level-up`, { confirm: true }); toast.success("Level Up emesso"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const eligible = status && (status.all_required_approved || status.required_count === 0);
  return (
    <div data-testid="teacher-certificates" className="space-y-2">
      <div className="flex items-center gap-2 text-gray-700"><Award size={14} /><span className="text-xs font-semibold uppercase tracking-wide">Attestati</span></div>
      {status && <div className="text-[11px] text-gray-500">Esercizi obbligatori approvati: {status.approved_count}/{status.required_count}</div>}
      <div className="space-y-1">
        {certs.map((c) => (
          <div key={c.id} className="flex justify-between items-center text-[11px] bg-gray-50 rounded-lg px-2 py-1">
            <span>{c.type === "frequency" ? "Frequenza" : "Level Up"} · {c.certificate_number}</span>
            <a href={fileHref(c.file_url)} target="_blank" rel="noreferrer" onClick={(e) => onAuthedFileClick(e, c.file_url, `Attestato-${c.certificate_number || "MAS"}.pdf`)} className="text-violet-700 inline-flex items-center gap-1"><Download size={12} /> PDF</a>
          </div>
        ))}
        {certs.length === 0 && <div className="text-[11px] text-gray-400">Nessun attestato.</div>}
      </div>
      <div className="flex gap-1 flex-wrap">
        <button data-testid={`teacher-cert-freq-${eid}`} onClick={issueFreq} className="btn-outline text-xs py-1 px-2">Attestato di frequenza</button>
        {course.level_up_enabled && <button data-testid={`teacher-cert-levelup-${eid}`} disabled={!eligible} onClick={() => setConfirm(true)} className="btn-primary text-xs py-1 px-2 disabled:opacity-40">Genera Level Up</button>}
      </div>
      {course.level_up_enabled && !eligible && <p className="text-[10px] text-amber-600">Level Up disponibile solo quando tutti gli esercizi obbligatori sono approvati.</p>}
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Confermi il percorso Level Up?">
        <p className="text-sm text-gray-600 mb-3">Confermi esplicitamente che l'allievo ha completato con successo tutti i requisiti del corso. Verrà generato l'Attestato Level Up.</p>
        <div className="flex gap-2"><button onClick={() => setConfirm(false)} className="btn-outline flex-1 text-sm">Annulla</button><button data-testid={`teacher-levelup-confirm-${eid}`} onClick={issueLevelUp} className="btn-primary flex-1 text-sm">Conferma e genera</button></div>
      </Modal>
    </div>
  );
}

/* ============================ EARNINGS ============================ */
function EarningsView() {
  const [e, setE] = useState(null);
  useEffect(() => { api.get("/academy/teacher/earnings").then((r) => setE(r.data)).catch((er) => toast.error(apiErr(er))); }, []);
  if (!e) return <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div>;
  const R = ({ label, value, strong }) => (
    <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0"><span className="text-sm text-gray-500">{label}</span><span className={`text-sm ${strong ? "font-bold text-gray-900" : "text-gray-800"}`}>{value}</span></div>
  );
  return (
    <div data-testid="teacher-earnings">
      <div className="flex items-center gap-2 mb-3 text-violet-700"><Wallet size={18} /><span className="text-sm font-semibold">Il mio compenso</span></div>
      <div className="card p-4">
        <R label="Quota maturata dai corsi" value={fmtEur(e.course_accrued)} />
        <R label="Quota maturata dall'assistenza" value={fmtEur(e.assistance_accrued)} />
        <R label="Totale maturato" value={fmtEur(e.total_accrued)} strong />
        <R label="Corrisposta" value={fmtEur(e.teacher_paid)} />
        <R label="Da corrispondere" value={fmtEur(Math.max(0, e.teacher_due))} strong />
      </div>
      {(e.clawbacks || []).length > 0 && (
        <div className="card p-4 mt-3" data-testid="teacher-clawbacks">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">Rettifiche da recuperare/compensare</div>
          {e.clawbacks.map((a, i) => (
            <div key={i} className="text-xs text-gray-700 py-1 border-b border-gray-100 last:border-0">
              {a.context === "course" ? "Corso" : "Assistenza"}{a.course_title ? ` · ${a.course_title}` : ""}: <b>{fmtEur(a.amount)}</b>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-3">Le quote vengono corrisposte da MAS. La conferma del pagamento è gestita esclusivamente dall'amministrazione.</p>
    </div>
  );
}

/* ============================ PROFILE ============================ */
function ProfileView() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/teacher/profile").then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/teacher/profile", { display_name: p.display_name, bio: p.bio, website_url: p.website_url, links: p.links }); toast.success("Profilo aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const uploadLogo = async (file) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Usa PNG, JPG o WEBP");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo troppo grande (max 2MB)");
    try { const data = await readFileDataUrl(file); await api.post("/teacher/logo", { data }); toast.success("Logo aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setLink = (i, k, v) => { const links = [...(p.links || [])]; links[i] = { ...links[i], [k]: v }; setP({ ...p, links }); };
  if (!p) return <div className="text-center text-gray-400 pt-10 text-sm">Carico…</div>;
  return (
    <div data-testid="teacher-profile" className="space-y-3">
      <div className="card p-4 space-y-2">
        <div className="flex items-center gap-3">
          {p.logo_url ? <img src={fileHref(p.logo_url)} alt="logo" className="w-14 h-14 rounded-xl object-cover border" data-testid="teacher-logo-preview" /> : <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center text-gray-300"><GraduationCap /></div>}
          <label className="btn-outline text-xs cursor-pointer inline-flex items-center gap-1" data-testid="teacher-logo-upload"><Upload size={12} /> Logo<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} /></label>
        </div>
        <label className="block text-xs text-gray-500">Nome visualizzato<input data-testid="teacher-display-name" className={inp} value={p.display_name || ""} onChange={(e) => setP({ ...p, display_name: e.target.value })} /></label>
        <label className="block text-xs text-gray-500">Bio<textarea className={inp} rows={2} value={p.bio || ""} onChange={(e) => setP({ ...p, bio: e.target.value })} /></label>
        <label className="block text-xs text-gray-500">Sito web (http/https)<input data-testid="teacher-website" className={inp} value={p.website_url || ""} onChange={(e) => setP({ ...p, website_url: e.target.value })} /></label>
        <div>
          <div className="text-xs text-gray-500 mb-1">Link</div>
          {(p.links || []).map((l, i) => (
            <div key={i} className="flex gap-1 mb-1">
              <input className={`${inp} mt-0`} placeholder="Etichetta" value={l.label || ""} onChange={(e) => setLink(i, "label", e.target.value)} />
              <input className={`${inp} mt-0`} placeholder="https://…" value={l.url || ""} onChange={(e) => setLink(i, "url", e.target.value)} />
              <button onClick={() => setP({ ...p, links: p.links.filter((_, j) => j !== i) })} className="btn-outline px-2"><Trash2 size={12} /></button>
            </div>
          ))}
          <button data-testid="teacher-link-add" onClick={() => setP({ ...p, links: [...(p.links || []), { label: "", url: "" }] })} className="btn-outline text-xs py-1 px-2">+ Aggiungi link</button>
        </div>
        <button data-testid="teacher-profile-save" disabled={busy} onClick={save} className="btn-primary text-sm w-full mt-1">{busy ? "Salvo…" : "Salva profilo"}</button>
      </div>
    </div>
  );
}
